/**
 * LocationIndexPage — /location/
 * Landing index of every Morgan Legal Group office + service area, grouped by region.
 */
import { useEffect } from 'react';
import { MapPin, Star, ArrowRight, Phone, Calendar } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { locationsByRegion, locations } from '@/data/locations';
import { pageDates } from '@/data/pageDates';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';
const metaTitle = 'Our Locations — NY Estate Planning & Probate Attorneys | Morgan Legal';
const metaDescription =
  'Morgan Legal Group serves all of New York — NYC, Long Island, the Hudson Valley, the Capital Region and Western New York. Find the office nearest you.';

export default function LocationIndexPage() {
  const canonical = `${SITE}/location/`;
  const dates = pageDates('/location/');
  const officeCount = locations.filter((l) => l.office).length;

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      url: canonical,
      name: metaTitle,
      description: metaDescription,
      isPartOf: { '@id': `${SITE}/#website` },
      datePublished: dates.published,
      dateModified: dates.modified,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Locations', item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: locations.map((l, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: l.title,
        url: `${SITE}${l.url}`,
      })),
    },
  ];

  useEffect(() => {
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: metaTitle, description: metaDescription, url: canonical });
    }
  }, [canonical]);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative bg-navy pt-28 lg:pt-32 pb-16 lg:pb-20 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
            <span className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap" style={{ opacity: 0.03 }}>
              LOCATIONS
            </span>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
              <a href="/" className="hover:text-gold transition-colors">Home</a>
              <span className="mx-2 text-gold/60">/</span>
              <span className="text-white/80">Locations</span>
            </nav>
            <div className="max-w-3xl">
              <div className="section-label mb-5 text-gold">
                <span className="inline-block w-10 h-0.5 bg-gold align-middle mr-2" />
                Statewide Reach, Local Counsel
              </div>
              <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
                Our Locations
              </h1>
              <p className="font-body text-white/85 text-lg leading-relaxed mb-8">
                Morgan Legal Group serves families across New York — from New York City and Long Island to the Hudson
                Valley, the Capital Region, and Western New York — with {officeCount} local offices, plus coordinated
                service for New Jersey and Florida clients. Find the office nearest you and book a free consultation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/appointment/" className="btn-gold">
                  <Calendar className="w-4 h-4" />
                  Schedule Your Consultation
                </a>
                <a href="tel:8885291315" className="btn-ghost-gold">
                  <Phone className="w-4 h-4" />
                  888-529-1315
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Regions */}
        {locationsByRegion.map(({ region, items }) =>
          items.length === 0 ? null : (
            <section key={region} className="py-14 lg:py-16 odd:bg-white even:bg-cream">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="section-label mb-3">{region}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((l) => (
                    <a key={l.slug} href={l.url} className="group flex flex-col bg-white border border-navy/10 hover:border-gold hover:shadow-xl transition-all duration-200 p-6">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-gold flex-shrink-0" />
                          <h3 className="font-display text-navy text-lg font-bold leading-tight">{l.shortName}</h3>
                        </div>
                        {l.office && (
                          <span className="flex items-center gap-1 text-xs font-body text-navy/60 whitespace-nowrap">
                            <Star className="w-3.5 h-3.5 fill-gold text-gold" />
                            {l.office.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                      {l.office ? (
                        <p className="font-body text-navy/60 text-sm mb-4">
                          {l.office.street}, {l.office.city}, {l.office.state} {l.office.zip}
                        </p>
                      ) : (
                        <p className="font-body text-navy/60 text-sm mb-4 italic">Service area · {l.areasServed.slice(0, 3).join(', ')}</p>
                      )}
                      <p className="font-body text-navy/70 text-sm leading-relaxed mb-5 line-clamp-3 flex-1">
                        {l.heroIntro.split('. ').slice(0, 2).join('. ')}.
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-gold font-body text-sm font-semibold mt-auto">
                        Read more
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </section>
          ),
        )}
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
